import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { BillMarkupType, MarkupBasedOn, ProviderMarkup } from '../../../models/medical-bill';
import { ICategory } from '../../../models/procedure';
import { BillMarkupRequest, PracticeRatesService } from '../../../services/practice-rates.service';

@Component({
    selector: 'app-practice-markup-form',
    standalone: true,
    imports: [FormsModule, NgIf, DialogModule, DropdownModule, InputNumberModule, CheckboxModule, ButtonModule, DividerModule],
    templateUrl: './practice-markup-form.component.html',
})
export class PracticeMarkupFormComponent implements OnInit, OnChanges
{
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    @Input() ein = '';
    @Input() practiceName = '';

    @Input() locationId?: number;
    @Input() npi?: string;
    @Input() editing?: ProviderMarkup | null;

    @Output() saved = new EventEmitter<void>();

    saving = false;

    categories: ICategory[] = [];
    categoryId: number | null = null;

    markupType: BillMarkupType = BillMarkupType.PercentOfBilled;
    markupBasedOn: MarkupBasedOn = MarkupBasedOn.AdjustedAmount;
    amount: number = 0;
    replaceExisting: boolean = false;
    extraDiscountSubsequentProcedures: boolean = true;
    subsequentProcedureDiscount: number = 0.5;

    readonly markupTypeOptions = [
        { label: 'Percent of Billed',            value: BillMarkupType.PercentOfBilled },
        { label: 'Fixed Amount',                  value: BillMarkupType.FixedAmount },
        { label: 'Flat Amount by Category',       value: BillMarkupType.FlatAmountByCategory },
        { label: 'Flat Amount by Category Unit',  value: BillMarkupType.FlatAmountByCategoryUnit },
    ];

    readonly markupBasedOnOptions = [
        { label: 'Adjusted Amount', value: MarkupBasedOn.AdjustedAmount },
        { label: 'Original Amount', value: MarkupBasedOn.OriginalAmount },
    ];

    get amountIsPercent(): boolean
    {
        return this.markupType === BillMarkupType.PercentOfBilled
            || this.markupType === BillMarkupType.ManualPercent;
    }

    get requiresCategory(): boolean
    {
        return this.markupType === BillMarkupType.FlatAmountByCategory
            || this.markupType === BillMarkupType.FlatAmountByCategoryUnit;
    }

    get canSave(): boolean
    {
        return !this.requiresCategory || !!this.categoryId;
    }

    get isLocationScoped(): boolean
    {
        return this.locationId != null && !!this.npi;
    }

    get isEditing(): boolean
    {
        return !!this.editing;
    }

    constructor(private _ratesService: PracticeRatesService) {}

    async ngOnInit()
    {
        this.categories = await this._ratesService.getCategories();
    }

    ngOnChanges(changes: SimpleChanges)
    {
        if (changes['editing'] && this.editing)
        {
            this.markupType = this.editing.markupType;
            this.categoryId = this.editing.category?.id ?? null;
            this.amount = this.editing.amount;
        }
        else if (changes['visible'] && this.visible && !this.editing)
        {
            this.markupType = BillMarkupType.PercentOfBilled;
            this.markupBasedOn = MarkupBasedOn.AdjustedAmount;
            this.categoryId = null;
            this.amount = 0;
            this.replaceExisting = false;
        }
    }

    async save()
    {
        if (!this.ein || !this.canSave) return;
        this.saving = true;

        const request: BillMarkupRequest = {
            markupType: this.markupType,
            markupBasedOn: this.markupBasedOn,
            categoryId: this.requiresCategory ? this.categoryId! : undefined,
            amount: this.amount,
            replaceExisting: this.isEditing || this.replaceExisting,
            extraDiscountSubsequentProcedures: this.extraDiscountSubsequentProcedures,
            subsequentProcedureDiscount: this.subsequentProcedureDiscount,
        };

        const save = this.isLocationScoped
            ? this._ratesService.addLocationMarkup(this.ein, this.locationId!, this.npi!, request)
            : this._ratesService.addPracticeMarkup(this.ein, request);

        await save.finally(() => this.saving = false);

        this.saved.emit();
        this.close();
    }

    close()
    {
        this.visible = false;
        this.visibleChange.emit(false);
    }
}
